<template>
  <div>
    <page-header :title="title" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input class="w-lg-auto" placeholder="Search societies or schools" v-model="searchTerm" @input="onSearch" />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>
              <div class="col-lg-auto">
                <b-form-select v-model="kind" :options="kindOptions" class="w-auto" @change="fetchSocieties()" />
              </div>
              <div class="col-lg-auto">
                <b-form-input v-model.trim="state" placeholder="State (GA)" class="w-auto" maxlength="2" style="width: 110px" @input="onSearch" />
              </div>

              <div class="col-lg d-flex flex-wrap justify-content-end">
                <b-btn class="ml-2" :to="{ name: 'sa-add-society' }" exact variant="primary" v-text="'Add Society'" />
              </div>
            </div>
          </div>

          <b-table
            :fields="tableColumns"
            :items="societies"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
          >
            <template #cell(name)="data">
              <strong>{{ data.item.name }}</strong>
              <div v-if="data.item.instagram" class="text-muted small">{{ data.item.instagram }}</div>
            </template>

            <template #cell(kind)="data">
              {{ kindLabel(data.item.kind) }}
            </template>

            <template #cell(university)="data">
              <span v-if="data.item.university">{{ data.item.university.name }}</span>
              <span v-else class="text-muted">Statewide</span>
            </template>

            <template #cell(state)="data">
              {{ data.item.state || '—' }}<span v-if="data.item.city"> · {{ data.item.city }}</span>
            </template>

            <template #cell(links)="data">
              <span>{{ get(data.item, 'pathways.length', 0) }} paths · {{ get(data.item, 'feeds_into.length', 0) }} feeder programs</span>
            </template>

            <template #cell(claim_state)="data">
              <b-badge variant="none" :class="data.item.claim_state === 'claimed' ? 'border border-success text-success' : 'border border-secondary text-secondary'">
                {{ data.item.claim_state === 'claimed' ? 'Claimed' : 'Unclaimed' }}
              </b-badge>
            </template>

            <template #cell(confidence)="data">
              <b-badge variant="none" :class="`border border-${confidenceVariant(data.item.confidence)} text-${confidenceVariant(data.item.confidence)}`">
                {{ data.item.confidence }}
              </b-badge>
            </template>

            <template #cell(is_active)="data">
              <b-badge
                :class="`border border-${data.item.is_active ? 'success' : 'danger'} text-${data.item.is_active ? 'success' : 'danger'}`"
                variant="none"
              >
                {{ data.item.is_active ? 'Active' : 'Inactive' }}
              </b-badge>
            </template>

            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <a :href="`https://transitionu.health/societies/${data.item.slug}`" target="_blank" rel="noopener" class="svg-icon mr-2" v-b-popover.hover.right :title="'View on TransitionU'">
                <i class="material-icons">open_in_new</i>
              </a>
              <router-link :to="{ name: 'sa-edit-society', params: { id: data.item.id } }" class="svg-icon mr-2" v-b-popover.hover.right :title="'Edit'">
                <i class="material-icons">edit</i>
              </router-link>
              <a href="#" @click.prevent="toggleActive(data.index)" v-b-popover.hover.right :title="data.item.is_active ? 'Deactivate' : 'Activate'">
                <md-icon :class="data.item.is_active ? 'text-danger' : 'text-success'">
                  {{ data.item.is_active ? 'block' : 'check_circle_outline' }}
                </md-icon>
              </a>
              <a href="#" @click.prevent="removeSociety(data.item)" v-b-popover.hover.right :title="'Delete'">
                <i class="material-icons text-danger">delete</i>
              </a>
            </template>
          </b-table>

          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalSocieties"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="societies-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '@/common/constants';

const KIND_LABEL = {
  school_chapter: 'School chapter',
  pre_nursing_club: 'Pre-nursing club',
  state_association: 'State association',
  national: 'National',
  other: 'Other',
};

export default {
  components: { MdIcon, PageHeader, Pagination },
  extends: Page,

  data() {
    return {
      title: 'Manage Societies',
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalSocieties: 0,
      searchTerm: '',
      kind: '',
      state: '',
      societies: [],
      kindOptions: [
        { value: '', text: 'All kinds' },
        { value: 'school_chapter', text: 'School chapters' },
        { value: 'pre_nursing_club', text: 'Pre-nursing clubs' },
        { value: 'state_association', text: 'State associations' },
        { value: 'national', text: 'National' },
        { value: 'other', text: 'Other' },
      ],
    };
  },

  computed: {
    tableColumns() {
      return [
        { key: 'name', label: 'Society' },
        { key: 'kind', label: 'Kind' },
        { key: 'university', label: 'School' },
        { key: 'state', label: 'State' },
        { key: 'links', label: 'Linked' },
        { key: 'claim_state', label: 'Claim' },
        { key: 'confidence', label: 'Confidence' },
        { key: 'is_active', label: 'Status' },
        { key: 'actions', tdClass: 'text-right', thStyle: { minWidth: '150px' } },
      ];
    },
  },

  methods: {
    get,
    ...mapActions('societies', ['getAllSocieties', 'updateSociety', 'deleteSociety']),

    kindLabel(k) {
      return KIND_LABEL[k] || k;
    },
    confidenceVariant(c) {
      return { high: 'success', med: 'warning', low: 'secondary' }[c] || 'secondary';
    },

    async fetchSocieties(pageNum = 1, params = {}) {
      this.isLoading = true;
      try {
        const response = await this.getAllSocieties({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.searchTerm && { search: this.searchTerm }),
          ...(this.kind && { kind: this.kind }),
          ...(this.state && this.state.length === 2 && { state: this.state.toUpperCase() }),
          ...params,
        });
        this.societies = get(response, 'data.results', []);
        this.currentPage = pageNum;
        this.totalSocieties = get(response, 'data.count', 0);
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchSocieties(pageNum);
    },

    onSearch() {
      this.debouncedSearch(this);
    },

    debouncedSearch: debounce(vm => {
      vm.fetchSocieties();
    }, 500),

    async toggleActive(index) {
      const current = this.societies[index];
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Mark "${current.name}" as ${current.is_active ? 'Inactive' : 'Active'}? Inactive societies are hidden on TransitionU.`,
          { title: 'Are you sure?', size: 'md', buttonSize: 'sm', okVariant: 'danger', okTitle: 'Yes', cancelTitle: 'No', footerClass: 'p-2', centered: true }
        );
        if (isConfirmed) {
          await this.updateSociety({ id: current.id, data: { is_active: !current.is_active } });
          this.fetchSocieties(this.currentPage);
          this.makeToast({ variant: 'success', msg: 'Status updated.' });
        }
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },

    async removeSociety(society) {
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(`Delete "${society.name}"? This removes it from TransitionU.`, {
          title: 'Are you sure?', size: 'md', buttonSize: 'sm', okVariant: 'danger', okTitle: 'Yes', cancelTitle: 'No', footerClass: 'p-2', centered: true,
        });
        if (isConfirmed) {
          await this.deleteSociety(society.id);
          this.fetchSocieties(this.currentPage);
          this.makeToast({ variant: 'success', msg: 'Society deleted.' });
        }
      } catch (_err) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
    },
  },

  async mounted() {
    this.fetchSocieties();
  },
};
</script>
